<script>
  import { afterUpdate, onMount } from "svelte";
  import {
    formatPrice,
    formatUnixTimestamp,
  } from "../../services/utilities.js";
  import { getEvent } from "../../models/events.js";
  import EventPurchaseButton from "../../components/EventPurchaseButton.svelte";
  import { getProduct } from "../../models/product.js";

  export let params;

  let event = null;
  let enrollment_open = false;
  let now = new Date().getTime() / 1000;
  let eventDone = false;

  /* Spotlight Puzzle Vars */
  let spotlightPuzzle = null;
  let spotlightPuzzleURL = null;
  let spotlightPuzzleRentalURL = null;
  let spotlightImageURL = null;
  let spotlightAttributes = {
    pieceCount: null,
    brand: null,
  };

  onMount(async () => {
    event = await getEvent(params.id);
    if (event && event.active) {
      document.title = `${event.title} | Missing Piece Puzzle Exchange`;
    }
    if (
      event.enrollment_start_ts &&
      event.enrollment_end_ts &&
      event.enrollment_start_ts <= now &&
      event.enrollment_end_ts > now
    ) {
      enrollment_open = true;
    }

    if (
      event.results &&
      (event.results.retail_sku || event.results.rental_sku)
    ) {
      let sku = event.results.retail_sku || event.results.rental_sku || null;
      spotlightPuzzle = await getProduct(sku, false);
    }

    if (event.end_ts < now) {
      eventDone = true;
    }
  });

  afterUpdate(() => {
    if (
      spotlightPuzzle &&
      spotlightPuzzleRentalURL == null &&
      spotlightPuzzleURL == null
    ) {
      setSpotlightAttributes();
      setImgURL("md");
      setSpotlightPuzzleURLs();
    }
  });

  function setImgURL(sz) {
    if (
      !spotlightPuzzle ||
      !spotlightPuzzle.images ||
      spotlightPuzzle.images.length === 0
    ) {
      return null;
    }
    let img = spotlightPuzzle.images[0];
    if (!img) return null;
    spotlightImageURL = `${img.url}${img.filename}-${sz}.${img.extension}`;
  }

  function setSpotlightPuzzleURLs() {
    if (!spotlightPuzzle) return null;
    if (event.results.retail_sku) {
      spotlightPuzzleURL = `/product/${spotlightPuzzle.title}/sku/${event.results.retail_sku}`;
    }
    if (event.results.rental_sku) {
      spotlightPuzzleRentalURL = `/product/${spotlightPuzzle.title}/sku/${event.results.rental_sku}`;
    }
  }

  function setSpotlightAttributes() {
    if (!spotlightPuzzle || !spotlightPuzzle.attributes) return null;
    let pieceCount = spotlightPuzzle.attributes.find((a) => a.name == "Piece Count");
    if (pieceCount && pieceCount.value) spotlightAttributes.pieceCount = pieceCount.value;
    let brand = spotlightPuzzle.attributes.find((a) => a.name == "Brand");
    if (brand && brand.value) spotlightAttributes.brand = brand.value;
  }
</script>

<svelte:head>
  {#if event && event.active}
    <title>{event.title} | Missing Piece Puzzle Exchange</title>
    <meta name="description" content={event.description ? event.description.replace(/<[^>]*>/g, '').slice(0, 160) : `${event.title} — a puzzle event by Missing Piece Puzzle Exchange.`} />
    <meta property="og:title" content="{event.title} | Missing Piece Puzzle Exchange" />
    <meta property="og:type" content="website" />
    <meta property="og:url" content="https://www.puzzleexchange.com/event/{params.id}" />
    <meta property="og:description" content={event.description ? event.description.replace(/<[^>]*>/g, '').slice(0, 160) : `${event.title} — a puzzle event by Missing Piece Puzzle Exchange.`} />
    <link rel="canonical" href="https://www.puzzleexchange.com/event/{params.id}" />
  {/if}
</svelte:head>

{#if event && event.active}
  <div class="container">

    <a class="back-link" href="/events">← All Events</a>

    <div class="event-header">
      {#if event.requires_tickets && event.purchased_quantity >= event.quantity}
        <span class="badge sold-out">Sold Out</span>
      {:else if event.requires_tickets}
        <span class="badge tickets">Tickets Required</span>
      {/if}

      <h1>{event.title}</h1>

      <div class="event-meta">
        <div class="meta-item">
          <span class="meta-label">When</span>
          <span class="meta-value">{formatUnixTimestamp(event.start_ts)} – {formatUnixTimestamp(event.end_ts)}</span>
        </div>
        <div class="meta-item">
          <span class="meta-label">Where</span>
          <span class="meta-value">{event.location}{event.virtual ? ' (Virtual)' : ''}</span>
        </div>
        {#if !eventDone && enrollment_open && event.requires_tickets && event.purchased_quantity < event.quantity}
          <div class="meta-item">
            <span class="meta-label">Tickets</span>
            <span class="meta-value availability">
              {event.quantity - event.purchased_quantity < 5 ? 'Less than 5 remaining' : 'Available'}
            </span>
          </div>
        {/if}
      </div>

      {#if !eventDone && enrollment_open && event.requires_tickets && event.purchased_quantity < event.quantity}
        <a class="btn" href="#tickets">Get Tickets</a>
      {/if}
    </div>


      <div class="event-description">
        {@html event.description}
      </div>

      {#if event.requires_tickets}
        <div class="ticket-section" id="tickets">
          {#if eventDone && event.results && event.results.winners}

            <h2 class="section-title">Competition Results</h2>
            <p class="section-intro">Congratulations to all teams that competed!</p>

            <div class="podium">
              {#if event.results.winners.second_place.name}
                <div class="podium-place second">
                  <svg class="piece-icon second-place"><use href="/icons.svg#puzzle-piece" /></svg>
                  <span class="podium-name">{event.results.winners.second_place.name}</span>
                  <span class="podium-time">{event.results.winners.second_place.time} min</span>
                  <div class="podium-block">2nd</div>
                </div>
              {/if}
              {#if event.results.winners.first_place.name}
                <div class="podium-place first">
                  <svg class="piece-icon first-place"><use href="/icons.svg#puzzle-piece" /></svg>
                  <span class="podium-name">{event.results.winners.first_place.name}</span>
                  <span class="podium-time">{event.results.winners.first_place.time} min</span>
                  <div class="podium-block">1st</div>
                </div>
              {/if}
              {#if event.results.winners.third_place.name}
                <div class="podium-place third">
                  <svg class="piece-icon third-place"><use href="/icons.svg#puzzle-piece" /></svg>
                  <span class="podium-name">{event.results.winners.third_place.name}</span>
                  <span class="podium-time">{event.results.winners.third_place.time} min</span>
                  <div class="podium-block">3rd</div>
                </div>
              {/if}
            </div>

            {#if spotlightImageURL}
              <div class="spotlight">
                <h2 class="section-title">Puzzle Spotlight</h2>
                <div class="spotlight-inner">
                  <img class="spotlight-img" alt={`${spotlightPuzzle.title}${spotlightAttributes.pieceCount ? ` ${spotlightAttributes.pieceCount} piece` : ""} jigsaw puzzle`} src={spotlightImageURL} />
                  <div class="spotlight-info">
                    {#if spotlightAttributes.brand}
                      <p class="spotlight-brand">{spotlightAttributes.brand}</p>
                    {/if}
                    <p class="spotlight-title">{spotlightPuzzle.title}{spotlightAttributes.pieceCount ? ` — ${spotlightAttributes.pieceCount} pieces` : ''}</p>
                    <div class="spotlight-actions">
                      {#if spotlightPuzzleURL}
                        <a class="btn" href={spotlightPuzzleURL}>Purchase</a>
                      {/if}
                      {#if spotlightPuzzleRentalURL}
                        <a class="btn" href={spotlightPuzzleRentalURL}>Rent</a>
                      {/if}
                    </div>
                  </div>
                </div>
              </div>
            {/if}

          {:else if event.purchased_quantity >= event.quantity}
            <div class="status-box">
              <h2 class="section-title">Tickets</h2>
              <p>This event is sold out. Stay tuned for future events!</p>
            </div>

          {:else if enrollment_open}
            <h2 class="section-title">Get Tickets</h2>
            <p class="section-intro">Last day to buy: <strong>{formatUnixTimestamp(event.enrollment_end_ts)}</strong></p>
            <div class="ticket-list">
              {#each event.tickets as ticket}
                {#if ticket.active}
                  <div class="ticket-card">
                    <div class="ticket-card-top">
                      <div>
                        <h3 class="ticket-name">{ticket.title}</h3>
                        <p class="ticket-desc">{ticket.description}</p>
                      </div>
                      <span class="ticket-price">{formatPrice(ticket.price)}</span>
                    </div>
                    <EventPurchaseButton product={ticket} eventID={params.id} eventName={event.title} />
                  </div>
                {/if}
              {/each}
            </div>

          {:else}
            <div class="status-box">
              {#if event.enrollment_start_ts > now}
                <p>Tickets go on sale <strong>{formatUnixTimestamp(event.enrollment_start_ts)}</strong>.</p>
              {:else if event.enrollment_end_ts < now}
                <p>Tickets are no longer available for this event.</p>
              {/if}
            </div>
          {/if}
        </div>
      {/if}

      {#if eventDone && event.results && event.results.images && event.results.images.length > 0}
        <div class="event-photos">
          <h2 class="section-title">Photos</h2>
          <div class="photo-grid">
            {#each event.results.images as img}
              <img class="event-img" alt={"Photo from " + event.title} src={img} />
            {/each}
          </div>
        </div>
      {/if}

  </div>
{/if}

<style type="text/less">
  .back-link {
    display: inline-block;
    font-size: 0.9rem;
    margin-bottom: var(--spacing);

    &:link, &:visited {
      text-decoration: none;
      color: var(--col-gray-40);
    }

    &:hover, &:focus {
      color: var(--col-text);
    }
  }

  .event-header {
    margin-bottom: var(--spacing-lg);
    padding-bottom: var(--spacing-lg);
    border-bottom: solid 1px var(--col-gray-90);

    h1 {
      margin: var(--spacing-sm) 0 var(--spacing);
    }
  }

  .badge {
    display: inline-block;
    font-size: 0.7rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.07em;
    padding: 0.25em 0.7em;
    border-radius: 3px;
  }

  .badge.sold-out {
    background: var(--col-gray-80);
    color: var(--col-gray-40);
  }

  .badge.tickets {
    background: var(--col-accent-1);
    color: white;
  }

  .event-meta {
    display: flex;
    flex-direction: column;
    gap: var(--spacing-sm);
    margin-bottom: var(--spacing);
  }

  .meta-item {
    display: flex;
    gap: var(--spacing-sm);
    align-items: baseline;
  }

  .meta-label {
    font-size: 0.7rem;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    color: var(--col-gray-50);
    min-width: 3.5rem;
    flex-shrink: 0;
  }

  .meta-value {
    font-size: 0.95rem;
    color: var(--col-text);
  }

  .meta-value.availability {
    color: var(--col-accent-1);
    font-weight: 600;
  }

  /* ── Content ── */
  .container {
    max-width: 800px;
    margin: 2rem auto;
    padding: 0 var(--spacing);
  }

  .event-description {
    margin-bottom: var(--spacing-lg);
    padding-bottom: var(--spacing-lg);
    border-bottom: solid 1px var(--col-gray-90);
  }

  .section-title {
    font-size: 1.1rem;
    font-weight: 700;
    margin: 0 0 var(--spacing-sm);
  }

  .section-intro {
    color: var(--col-gray-40);
    margin: 0 0 var(--spacing);
  }

  /* ── Tickets ── */
  .ticket-section {
    margin-bottom: var(--spacing-lg);
    padding-bottom: var(--spacing-lg);
    border-bottom: solid 1px var(--col-gray-90);
  }

  .ticket-list {
    display: flex;
    flex-direction: column;
    gap: var(--spacing);
    margin-top: var(--spacing);
  }

  .ticket-card {
    padding: var(--spacing-lg);
    border: solid 1px var(--col-gray-90);
    border-left: 4px solid var(--col-accent-2);
    background: var(--col-gray-98);
    border-radius: var(--border-radius);
  }

  .ticket-card-top {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: var(--spacing);
    margin-bottom: var(--spacing-sm);
  }

  .ticket-name {
    margin: 0 0 0.25rem;
    font-size: 1.1rem;
  }

  .ticket-desc {
    margin: 0;
    color: var(--col-gray-40);
    font-size: 0.9rem;
  }

  .ticket-price {
    font-weight: 700;
    font-size: 1.3rem;
    white-space: nowrap;
    color: var(--col-text);
  }

  .status-box {
    padding: var(--spacing-lg);
    background: var(--col-gray-98);
    border: solid 1px var(--col-gray-90);
    border-radius: var(--border-radius);
    color: var(--col-gray-50);

    p { margin: 0; }
  }

  /* ── Podium ── */
  .podium {
    display: flex;
    align-items: flex-end;
    justify-content: center;
    gap: var(--spacing-sm);
    margin: var(--spacing-lg) 0;
  }

  .podium-place {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.4rem;
    flex: 1;
    max-width: 200px;
    text-align: center;
  }

  .podium-name {
    font-weight: 600;
    font-size: 0.95rem;
  }

  .podium-time {
    font-size: 0.8rem;
    color: var(--col-gray-50);
  }

  .podium-block {
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 700;
    font-size: 0.85rem;
    color: white;
    border-radius: var(--border-radius) var(--border-radius) 0 0;
  }

  .podium-place.first .podium-block {
    height: 80px;
    background: var(--col-gold, #c9a227);
  }

  .podium-place.second .podium-block {
    height: 56px;
    background: var(--col-silver, #a8a9ad);
  }

  .podium-place.third .podium-block {
    height: 40px;
    background: var(--col-bronze, #cd7f32);
  }

  .piece-icon {
    width: 2rem;
    height: 2rem;
    flex-shrink: 0;
    stroke: var(--col-bg);
    stroke-width: 0.5em;
  }

  .first-place { fill: var(--col-gold, #c9a227); }
  .second-place { fill: var(--col-silver, #a8a9ad); }
  .third-place { fill: var(--col-bronze, #cd7f32); }

  .concluded-note {
    color: var(--col-gray-50);
  }

  /* ── Spotlight ── */
  .spotlight {
    margin-top: var(--spacing-lg);
    padding-top: var(--spacing-lg);
    border-top: solid 1px var(--col-gray-90);
  }

  .spotlight-inner {
    display: flex;
    gap: var(--spacing-lg);
    align-items: flex-start;
    margin-top: var(--spacing);
  }

  .spotlight-img {
    width: 180px;
    height: auto;
    border-radius: var(--border-radius);
    flex-shrink: 0;
  }

  .spotlight-info {
    display: flex;
    flex-direction: column;
    gap: var(--spacing-sm);

    p { margin: 0; }
  }

  .spotlight-brand {
    font-size: 0.8rem;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: var(--col-gray-50);
  }

  .spotlight-title {
    font-weight: 600;
    font-size: 1.05rem;
  }

  .spotlight-actions {
    display: flex;
    gap: var(--spacing-sm);
    flex-wrap: wrap;
  }

  /* ── Photos ── */
  .event-photos {
    margin-top: var(--spacing-lg);
  }

  .photo-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: var(--spacing-sm);
    margin-top: var(--spacing);
  }

  .event-img {
    width: 100%;
    aspect-ratio: 4/3;
    object-fit: cover;
    border-radius: var(--border-radius);
  }

  @media (max-width: 600px) {
    .event-header h1 {
      font-size: 1.5rem;
    }

    .podium {
      gap: 4px;
    }

    .spotlight-inner {
      flex-direction: column;
    }

    .spotlight-img {
      width: 100%;
    }

    .photo-grid {
      grid-template-columns: repeat(2, 1fr);
    }

    .ticket-card-top {
      flex-direction: column;
    }
  }
</style>
